import { useReducedMotion } from 'motion/react';
import { type MouseEvent, memo, type PointerEvent, useCallback, useEffect, useRef, useState } from 'react';

import { FilePenLineIcon, XIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { cn } from '@air/utils-tailwind';

import {
  WorkspaceAgentAirMark,
  type WorkspaceAgentAirMarkHandle,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/WorkspaceAgentAirMark';
import agenticStyles from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/workspaceAgentStartTaskCta.module.css';

export const UPLOADED_BATCH_OFFER_PILL_LABEL = 'Auto tag and rename';

/** One play of `AgentRolloverBurst` (91 frames at 60fps) before the file icon returns. */
const UPLOADED_BATCH_OFFER_PILL_MARK_INTRO_MS = 1517;

export type UploadedBatchOfferPillProps = {
  label: string;
  onClick: () => void;
  onDismiss: () => void;
  /** True after the pill has finished pushing out, so the burst plays while it is visible. */
  playMarkIntro: boolean;
};

export const UploadedBatchOfferPill = memo(
  ({ label, onClick, onDismiss, playMarkIntro }: UploadedBatchOfferPillProps) => {
    const shouldReduceMotion = !!useReducedMotion();
    const markRef = useRef<WorkspaceAgentAirMarkHandle>(null);
    const [isIntroPlaying, setIsIntroPlaying] = useState(false);

    useEffect(() => {
      if (!playMarkIntro || shouldReduceMotion) {
        return;
      }

      setIsIntroPlaying(true);
      markRef.current?.replay();

      const timer = window.setTimeout(() => {
        setIsIntroPlaying(false);
      }, UPLOADED_BATCH_OFFER_PILL_MARK_INTRO_MS);

      return () => {
        window.clearTimeout(timer);
      };
    }, [playMarkIntro, shouldReduceMotion]);

    const handlePointerEnter = useCallback(() => {
      markRef.current?.replay();
    }, []);

    const handlePointerDown = useCallback((event: PointerEvent<HTMLButtonElement>) => {
      event.stopPropagation();
    }, []);

    const handleDismiss = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onDismiss();
      },
      [onDismiss],
    );

    return (
      <span
        className={cn(agenticStyles.agenticBlueCta, 'group relative inline-flex rounded-full')}
        onPointerEnter={handlePointerEnter}
      >
        <Button
          appearance="filled"
          className="h-7 gap-1 rounded-full border-0 ads:bg-agentic bg-[var(--color-agentic-blue)] px-2.5 ads:text-inverse text-14 text-white transition-[padding] duration-200 ease-out ads:hover:bg-hover-agentic hover:bg-[var(--color-agentic-blue-hover)] ads:hover:text-inverse hover:text-white active:bg-[var(--color-agentic-blue-hover)] ads:active:bg-hover-agentic group-focus-within:pr-[30px] group-hover:pr-[30px] motion-reduce:transition-none"
          color="gray"
          data-testid="UPLOADED_BATCH_OFFER_PILL"
          onClick={onClick}
          onPointerDown={handlePointerDown}
          size="small"
          type="button"
        >
          <span className="relative size-4 shrink-0">
            <span
              className={cn(
                'absolute inset-0 flex items-center justify-center transition-opacity',
                isIntroPlaying ? 'opacity-0' : 'opacity-100 group-focus-within:opacity-0 group-hover:opacity-0',
              )}
            >
              <FilePenLineIcon aria-hidden className="size-4" />
            </span>
            <span
              className={cn(
                'absolute inset-0 flex items-center justify-center transition-opacity',
                isIntroPlaying ? 'opacity-100' : 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100',
              )}
            >
              <WorkspaceAgentAirMark ref={markRef} />
            </span>
          </span>
          {label}
        </Button>
        <button
          aria-label="Dismiss"
          className="pointer-events-none absolute top-1/2 right-2.5 flex size-4 -translate-y-1/2 items-center justify-center rounded-full text-white opacity-0 transition-opacity duration-200 ease-out hover:bg-white/15 focus-visible:pointer-events-auto focus-visible:bg-white/15 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 motion-reduce:transition-none"
          data-testid="UPLOADED_BATCH_OFFER_PILL_DISMISS"
          onClick={handleDismiss}
          onPointerDown={handlePointerDown}
          type="button"
        >
          <XIcon aria-hidden size="xs" />
        </button>
      </span>
    );
  },
);

UploadedBatchOfferPill.displayName = 'UploadedBatchOfferPill';
